import {
  AuditTrailChangeReasons,
  AuditTrailFields,
  AuditTrailItemType,
  IAuditTrailWrapper,
  IAuditTrailObjectBase,
  IChangeDocument
} from '@root/query/auditTrail'
import { Step, StepBody, StepIconWrapper, StepRow } from './AuditTrailStep.styled'
import { FaCircle, FaFlag, FaLock, FaLockOpen } from 'react-icons/fa'
import { AttachmentBlock } from './ContentBlocks/AttachmentBlock'
import { DiscontinuationHeaderBlock, HeaderBlock } from './ContentBlocks/Header/HeaderBlock'
import { AuditTrailComment } from '../AuditTrailComment'
import { IUsershipPerson } from '@root/query'
import { TagBlock } from './ContentBlocks/TagBlock'
import { findRuleChange, RuleBlock } from './ContentBlocks/RuleBlock'
import theme from '@root/styles/theme'
import type { CSSProperties } from 'react'

export interface IBaseBlockProps {
  slug: string
}

interface Props {
  isLast?: boolean
  auditRecord: IAuditTrailWrapper
  users: IUsershipPerson[]
  seenAt: string | null
}

export const AuditTrailStep: React.FC<Props> = ({ isLast = false, auditRecord, users, seenAt }) => {
  const getIcon = (record?: IAuditTrailObjectBase<IChangeDocument>) => {
    const change = record?.changes?.[0]

    if (!change) {
      return null
    }

    if (change.field === AuditTrailFields.IS_FLAGGED) {
      return {
        icon: <FaFlag />,
        color: change.new_values ? theme.colors.error : theme.colors.metalGray
      }
    }

    if (change.field === AuditTrailFields.IS_LOCKED) {
      return {
        icon: change.new_values ? <FaLock /> : <FaLockOpen />,
        color: theme.colors.metalGray
      }
    }

    return null
  }

  /*
   * Rendering logic
   */
  const isAuditAction = auditRecord.type === AuditTrailItemType.ACTIVITY
  const isComment = auditRecord.type === AuditTrailItemType.COMMENT
  const isBeginningOfHistory = auditRecord.type === AuditTrailItemType.HISTORY_BEGIN
  const actionItem = isAuditAction ? auditRecord.item : undefined
  const actionIcon = getIcon(actionItem)
  const timelineIcon = actionIcon ?? {
    icon: <FaCircle />,
    color: theme.colors.neutralGray
  }
  const baseIconSize = (theme.spacing.sm + theme.spacing.md) / 2 - theme.spacing.xxxs
  const baseWrapperSize = theme.spacing.lg
  const emptyIconStyle: CSSProperties | undefined = !actionIcon
    ? ({
        '--audit-icon-size': `${baseIconSize + theme.spacing.xxxs}rem`,
        '--audit-icon-wrapper-size': `${baseWrapperSize + theme.spacing.xxxs}rem`
      } as CSSProperties)
    : undefined

  let blockToRender = null

  if (isAuditAction) {
    switch (auditRecord.item.changes[0].field) {
      case AuditTrailFields.ATTACHMENTS:
        blockToRender = <AttachmentBlock auditRecord={auditRecord.item} />
        break
      case AuditTrailFields.TAGS:
        blockToRender = <TagBlock auditRecord={auditRecord.item} />
        break
      default:
        break
    }

    const hasRuleChange = findRuleChange(auditRecord.item.changes)
    if (
      (auditRecord.item.change_reason === AuditTrailChangeReasons.FROM_RULE_MATCH &&
        hasRuleChange) ||
      (auditRecord.item.change_reason === AuditTrailChangeReasons.RULE_MATCH_WAIT_ATTACHMENT &&
        hasRuleChange)
    ) {
      blockToRender = <RuleBlock auditRecord={auditRecord.item} />
    }
  }

  if (isComment) {
    blockToRender = <AuditTrailComment comment={auditRecord.item} users={users} />
  }

  return (
    <>
      {isBeginningOfHistory ? (
        <Step style={emptyIconStyle}>
          <StepRow>
            <StepIconWrapper $color={timelineIcon.color}>{timelineIcon.icon}</StepIconWrapper>
            <DiscontinuationHeaderBlock date={auditRecord.time} message={auditRecord.message} />
          </StepRow>
          <StepBody $dashed={true} $isLast={isLast} />
        </Step>
      ) : (
        <Step style={emptyIconStyle}>
          <StepRow>
            <StepIconWrapper $color={timelineIcon.color}>{timelineIcon.icon}</StepIconWrapper>
            <HeaderBlock auditRecord={auditRecord} seenAt={seenAt} />
          </StepRow>
          <StepBody $dashed={false} $isLast={isLast}>
            {blockToRender}
          </StepBody>
        </Step>
      )}
    </>
  )
}
